import { useState, useEffect, useRef } from 'react';

const C = {
  green:"#16320f",greenLight:"#1f4516",greenBorder:"#3a7029",
  gold:"#d9c20d",bg:"#0c1e09",surface:"#111f0d",
  text:"#e8f0e6",textMid:"#8faa8a",textDim:"#4a6646",teal:"#34d399",
};
const AGE_GROUP_COLORS = {"10U":"#34d399","12U":"#d9c20d","14U":"#60a5fa","16U":"#f97316","18U":"#a78bfa"};
const AGE_GROUP_ORDER = {"10U":0,"12U":1,"14U":2,"16U":3,"18U":4};

function fmtDateRange(start, end) {
  if (!start) return '';
  const opts = { month: 'short', day: 'numeric' };
  const optsY = { month: 'short', day: 'numeric', year: 'numeric' };
  const [sy,sm,sd] = start.split('-').map(Number);
  const s = new Date(sy,sm-1,sd);
  if (!end||end===start) return s.toLocaleDateString('en-US',optsY);
  const [ey,em,ed] = end.split('-').map(Number);
  const e = new Date(ey,em-1,ed);
  return `${s.toLocaleDateString('en-US',opts)} – ${e.toLocaleDateString('en-US',optsY)}`;
}

function CollegeLogo({ logoUrl, name, size = 18 }) {
  if (!logoUrl) return null;
  return <img src={logoUrl} alt={name || ''} width={size} height={size} style={{ objectFit: 'contain', verticalAlign: 'middle', marginRight: 4, borderRadius: 2, flexShrink: 0 }} />;
}

// Group a player list by grad year, descending soonest-first (Class of 2028, 2027, ... then no
// year at end). Shared by the ATHLETE PROFILES and ALUMNI sections — same grouping/sort for both.
function groupByClassYear(players) {
  const playersByClass = players.reduce((acc, p) => {
    const yr = p.gradYear ? String(p.gradYear) : 'TBD';
    (acc[yr] = acc[yr] || []).push(p);
    return acc;
  }, {});
  const classYears = Object.keys(playersByClass).sort((a, b) => {
    if (a === 'TBD') return 1;
    if (b === 'TBD') return -1;
    return Number(a) - Number(b); // ascending — graduating soonest first
  });
  classYears.forEach(yr => {
    playersByClass[yr].sort((a, b) => (a.name||'').split(' ').pop().localeCompare((b.name||'').split(' ').pop()));
  });
  return { playersByClass, classYears };
}

// Renders the "CLASS OF ____" grouped card grid — shared by ATHLETE PROFILES and ALUMNI so the two
// sections stay visually identical without duplicated markup.
function ClassYearGroups({ classYears, playersByClass }) {
  return (
    <>
      {classYears.map(yr => (
        <div key={yr} style={{ marginBottom: 28 }}>
          {/* Class header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 18, letterSpacing: 3, color: C.gold }}>
              {yr === 'TBD' ? 'CLASS YEAR TBD' : `CLASS OF ${yr}`}
            </div>
            <div style={{ flex: 1, height: 1, background: C.greenBorder + '55' }} />
            <div style={{ fontSize: 10, color: C.textDim, fontFamily: "'DM Mono',monospace" }}>
              {playersByClass[yr].length} athlete{playersByClass[yr].length !== 1 ? 's' : ''}
            </div>
          </div>
          {/* Cards grid — tighter for large rosters */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))', gap: 8 }}>
            {playersByClass[yr].map((p, i) => (
              <a key={i} href={p.shareToken ? `/recruit/${p.shareToken}?from=${encodeURIComponent(window.location.href)}&label=Recruiting+Page` : '#'} className="player-card"
                style={{ background: C.surface, border: `1px solid ${p.committedSchool ? C.gold+'55' : C.greenBorder}`, borderRadius: 5, padding: 10, textDecoration: 'none', display: 'block', transition: 'border-color 0.15s' }}>
                {p.photoUrl && (
                  <div style={{ width: 96, height: 96, borderRadius: 4, overflow: 'hidden', margin: '0 auto 8px', border: `1px solid ${C.greenBorder}` }}>
                    <img src={p.photoUrl} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} onError={e => { e.target.parentNode.style.display='none'; }} />
                  </div>
                )}
                {/* Name + positions grouped left; committed badge floats right */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4, marginBottom: 4 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 14, letterSpacing: 1.5, color: C.text, lineHeight: 1.1, marginBottom: 3 }}>{p.name}</div>
                    {(p.positions||[]).length > 0 && (
                      <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        {(p.positions||[]).map(pos => <span key={pos} style={{ fontSize: 8, padding: '1px 5px', borderRadius: 2, border: `1px solid ${C.greenBorder}`, color: C.textMid, fontFamily: "'DM Mono',monospace" }}>{pos}</span>)}
                      </div>
                    )}
                  </div>
                  {p.committedSchool && (
                    <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <span style={{ fontSize: 7, color: C.gold, fontFamily: "'DM Mono',monospace", letterSpacing: 0.5, background: C.gold+'22', border: `1px solid ${C.gold}44`, borderRadius: 2, padding: '1px 4px', whiteSpace: 'nowrap' }}>COMMITTED</span>
                      {p.committedSchool.logoUrl
                        ? <img src={p.committedSchool.logoUrl} alt={p.committedSchool.name} width={32} height={32} style={{ objectFit: 'contain', background: '#fff', borderRadius: 3, padding: 2 }} onError={e => { e.target.style.display='none'; }} />
                        : <div style={{ fontSize: 8, color: C.gold, textAlign: 'center', maxWidth: 50, wordBreak: 'break-word', lineHeight: 1.2 }}>{p.committedSchool.name}</div>
                      }
                    </div>
                  )}
                </div>
                {p.bio && <div style={{ fontSize: 11, color: C.textDim, lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{p.bio}</div>}
              </a>
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

export default function OrgProfile() {
  const [org, setOrg] = useState(null);
  const [players, setPlayers] = useState([]);
  const [alumniPlayers, setAlumniPlayers] = useState([]);
  const [sheets, setSheets] = useState([]);
  const [weekendTeams, setWeekendTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pastExpanded, setPastExpanded] = useState(false);
  const gcSdkRef = useRef(false);

  useEffect(() => {
    fetch('/api/org-profile')
      .then(r => r.ok ? r.json() : Promise.reject('Failed to load'))
      .then(data => {
        setOrg(data.org || {});
        setPlayers(data.publicPlayers || []);
        setAlumniPlayers(data.alumniPlayers || []);
        setSheets(data.publishedSheets || []);
        setWeekendTeams(data.weekendTeams || []);
        setLoading(false);
      })
      .catch(() => { setError('Failed to load profile.'); setLoading(false); });
  }, []);

  // Load GC SDK and init widgets once weekend teams are known
  useEffect(() => {
    const widgetTeams = weekendTeams.filter(t => t.hasWidget);
    if (!widgetTeams.length || gcSdkRef.current) return;
    gcSdkRef.current = true;
    const script = document.createElement('script');
    script.src = 'https://widgets.gc.com/static/js/sdk.v1.js';
    script.onload = () => {
      widgetTeams.forEach(team => {
        if (window.GC?.team?.schedule?.init) {
          window.GC.team.schedule.init({
            target: `#${team.gcWidgetDivId}`,
            widgetId: team.gcWidgetId,
            maxVerticalGamesVisible: team.gcWidgetMaxGames || 4,
          });
        }
      });
    };
    document.body.appendChild(script);
  }, [weekendTeams]);

  // Grouping/sort logic lives in groupByClassYear (shared with the ALUMNI section below).
  const { playersByClass, classYears } = groupByClassYear(players);
  const { playersByClass: alumniByClass, classYears: alumniClassYears } = groupByClassYear(alumniPlayers);

  // Group weekend teams by tournament, sort by age group within each tournament
  const weekendByTournament = weekendTeams.reduce((acc, team) => {
    const key = team.tournamentId;
    if (!acc[key]) acc[key] = { tournamentName: team.tournamentName, tournamentId: team.tournamentId, teams: [] };
    acc[key].teams.push(team);
    return acc;
  }, {});
  Object.values(weekendByTournament).forEach(group => {
    group.teams.sort((a, b) => (AGE_GROUP_ORDER[a.ageGroup] ?? 99) - (AGE_GROUP_ORDER[b.ageGroup] ?? 99));
  });
  const weekendGroups = Object.values(weekendByTournament)
    .sort((a, b) => a.tournamentName.localeCompare(b.tournamentName));

  // Group sheets by tournament, sort teams alpha within each group
  const sheetsByTournament = sheets.reduce((acc, s) => {
    const key = s.tournamentId || s.tournamentName || '';
    (acc[key] = acc[key] || []).push(s);
    return acc;
  }, {});
  Object.values(sheetsByTournament).forEach(group =>
    group.sort((a, b) => (a.teamName||'').localeCompare(b.teamName||''))
  );
  const todayStr = new Date().toISOString().split('T')[0];
  const allGroups = Object.values(sheetsByTournament);
  // Upcoming: tournament end date is today or future; sort ascending (nearest first)
  const upcomingGroups = allGroups
    .filter(g => (g[0].endDate || g[0].startDate || '') >= todayStr)
    .sort((a, b) => (a[0].startDate||'').localeCompare(b[0].startDate||''));
  // Past: tournament already ended; sort descending (most recent first)
  const pastGroups = allGroups
    .filter(g => (g[0].endDate || g[0].startDate || '') < todayStr)
    .sort((a, b) => (b[0].startDate||'').localeCompare(a[0].startDate||''));
  // Feature all tournaments in the same weekend as the nearest upcoming one
  // "Same weekend" = startDate within 4 days of the nearest startDate
  const nearestStart = upcomingGroups[0]?.[0]?.startDate || null;
  const windowEndStr = nearestStart
    ? new Date(new Date(nearestStart + 'T00:00:00').getTime() + 4 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    : null;
  const featuredGroups = windowEndStr
    ? upcomingGroups.filter(g => g[0].startDate <= windowEndStr)
    : [];

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.text, fontFamily: 'Inter,-apple-system,sans-serif' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Mono:wght@400;500&family=Inter:wght@400;500;600&display=swap');
        *{box-sizing:border-box;}
        a{color:${C.teal};}
        .player-card:hover{border-color:${C.teal}66!important;}
      `}</style>

      {/* Header */}
      <div style={{ background: C.greenLight, borderBottom: `2px solid ${C.gold}`, padding: '0 20px', display: 'flex', alignItems: 'center', gap: 12, minHeight: 56 }}>
        <img src="/warriors-logo.png" style={{ width: 36, height: 36, objectFit: 'contain' }} alt="Warrior Academy" onError={e => { e.target.style.display='none'; }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 20, letterSpacing: 4, color: C.gold, lineHeight: 1 }}>Warrior Academy</div>
          <div style={{ fontSize: 10, color: C.textDim, letterSpacing: 2 }}>RECRUITING</div>
        </div>
        <a href="/warriors/schedule" style={{ fontSize: 10, color: C.teal, textDecoration: 'none', fontFamily: "'DM Mono',monospace", letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 4, border: `1px solid ${C.teal}44`, borderRadius: 3, padding: '5px 10px', flexShrink: 0 }}>📅 SCHEDULE</a>
      </div>

      <div style={{ maxWidth: 860, margin: '0 auto', padding: '28px 16px' }}>
        {loading && <div style={{ textAlign: 'center', padding: '80px 0', color: C.textDim, fontSize: 13 }}>Loading…</div>}
        {error && <div style={{ textAlign: 'center', padding: '80px 0', color: C.textDim, fontSize: 14 }}>{error}</div>}

        {!loading && !error && (
          <>
            {/* Org Info */}
            <div style={{ background: C.surface, border: `1px solid ${C.greenBorder}`, borderRadius: 8, padding: 24, marginBottom: 24 }}>
              <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 32, letterSpacing: 4, color: C.text, lineHeight: 1.1, marginBottom: 10 }}>
                {org?.name || 'Warrior Academy Softball'}
              </div>
              {org?.description && (
                <div style={{ fontSize: 14, color: C.textMid, lineHeight: 1.7, marginBottom: 14, whiteSpace: 'pre-wrap' }}>{org.description}</div>
              )}
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {org?.website && <a href={org.website} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: C.teal, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>🌐 {org.website.replace(/^https?:\/\//,'')}</a>}
                {org?.instagram && <a href={`https://instagram.com/${org.instagram.replace(/^@/,'')}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: C.teal, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 5 }}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="14" height="14" style={{ flexShrink: 0 }}><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                  {org.instagram.startsWith('@') ? org.instagram : `@${org.instagram}`}
                </a>}
                {org?.contactEmail && <a href={`mailto:${org.contactEmail}`} style={{ fontSize: 12, color: C.teal, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>✉️ {org.contactEmail}</a>}
              </div>
            </div>

            {/* Featured team sheets — all tournaments in the current/nearest weekend */}
            {featuredGroups.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <div style={{ fontSize: 10, color: C.textDim, letterSpacing: 3, fontFamily: "'DM Mono',monospace" }}>TEAM SHEETS</div>
                  <div style={{ flex: 1, height: 1, background: C.greenBorder + '55' }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {featuredGroups.map((group, gi) => {
                    const rep = group[0];
                    return (
                      <div key={gi} style={{ background: C.surface, border: `1px solid ${C.greenBorder}`, borderRadius: 8, overflow: 'hidden' }}>
                        <div style={{ padding: '10px 16px', background: C.greenLight, borderBottom: `1px solid ${C.greenBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                          <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 12, color: C.gold, letterSpacing: 1.5, fontWeight: 600 }}>{rep.tournamentName}</div>
                          <div style={{ fontSize: 10, color: C.textDim }}>{fmtDateRange(rep.startDate, rep.endDate)}</div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          {group.map((s, i) => (
                            <div key={i} style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, borderBottom: i < group.length - 1 ? `1px solid ${C.greenBorder}44` : 'none' }}>
                              <div style={{ fontSize: 13, color: C.text, fontWeight: 500 }}>{s.teamName}</div>
                              <a href={`/warriors/team/${s.teamId}/${s.tournamentId}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 10, letterSpacing: 1, background: C.teal+'22', border: `1px solid ${C.teal}55`, borderRadius: 3, color: C.teal, padding: '5px 12px', textDecoration: 'none', fontFamily: "'DM Mono',monospace", flexShrink: 0 }}>
                                TEAM SHEET ↗
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Past team sheets — collapsible archive */}
            {pastGroups.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <button onClick={() => setPastExpanded(e => !e)} style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginBottom: pastExpanded ? 12 : 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ fontSize: 10, color: C.textDim, letterSpacing: 3, fontFamily: "'DM Mono',monospace" }}>PAST TEAM SHEETS</div>
                    <div style={{ flex: 1, height: 1, background: C.greenBorder + '55' }} />
                    <div style={{ fontSize: 10, color: C.textDim, fontFamily: "'DM Mono',monospace", letterSpacing: 1 }}>{pastExpanded ? '▲ HIDE' : `▼ ${pastGroups.length} TOURNAMENT${pastGroups.length !== 1 ? 'S' : ''}`}</div>
                  </div>
                </button>
                {pastExpanded && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {pastGroups.map((group, gi) => {
                      const rep = group[0];
                      return (
                        <div key={gi} style={{ background: C.surface, border: `1px solid ${C.greenBorder}44`, borderRadius: 6, overflow: 'hidden', opacity: 0.85 }}>
                          <div style={{ padding: '8px 14px', background: C.greenLight, borderBottom: `1px solid ${C.greenBorder}44`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: C.textMid, letterSpacing: 1 }}>{rep.tournamentName}</div>
                            <div style={{ fontSize: 10, color: C.textDim }}>{fmtDateRange(rep.startDate, rep.endDate)}</div>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            {group.map((s, i) => (
                              <div key={i} style={{ padding: '8px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, borderBottom: i < group.length - 1 ? `1px solid ${C.greenBorder}33` : 'none' }}>
                                <div style={{ fontSize: 13, color: C.textMid }}>{s.teamName}</div>
                                <a href={`/warriors/team/${s.teamId}/${s.tournamentId}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 9, letterSpacing: 1, background: 'transparent', border: `1px solid ${C.greenBorder}`, borderRadius: 3, color: C.textDim, padding: '4px 10px', textDecoration: 'none', fontFamily: "'DM Mono',monospace", flexShrink: 0 }}>
                                  VIEW ↗
                                </a>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* THIS WEEKEND */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <div style={{ fontSize: 10, color: C.gold, letterSpacing: 3, fontFamily: "'DM Mono',monospace", fontWeight: 600 }}>THIS WEEKEND</div>
                <div style={{ flex: 1, height: 1, background: C.gold + '33' }} />
                <a href="/warriors/schedule" style={{ fontSize: 9, color: C.teal, textDecoration: 'none', fontFamily: "'DM Mono',monospace", letterSpacing: 1 }}>📅 VIEW FULL SCHEDULE →</a>
              </div>
              {weekendTeams.length === 0 ? (
                <div style={{ fontSize: 13, color: C.textDim, padding: '12px 0', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span>No games scheduled this weekend.</span>
                  <a href="/warriors/schedule" style={{ fontSize: 11, color: C.teal, fontFamily: "'DM Mono',monospace", letterSpacing: 1 }}>VIEW FULL SCHEDULE →</a>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  {weekendGroups.map(group => (
                    <div key={group.tournamentId}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                        <div style={{ fontSize: 11, color: C.textMid, letterSpacing: 2, fontFamily: "'DM Mono',monospace", fontWeight: 600 }}>🏆 {group.tournamentName}</div>
                        <div style={{ flex: 1, height: 1, background: C.greenBorder + '44' }} />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(min(360px,100%),1fr))', gap: 14 }}>
                        {group.teams.map((team, i) => {
                          const agColor = AGE_GROUP_COLORS[team.ageGroup] || C.gold;
                          return (
                            <div key={i} style={{ background: C.surface, border: `1px solid ${C.greenBorder}`, borderRadius: 6, overflowX: 'auto' }}>
                              <div style={{ padding: '8px 12px', borderBottom: `1px solid ${C.greenBorder}`, display: 'flex', alignItems: 'center', gap: 8 }}>
                                {team.ageGroup && (
                                  <span style={{ fontSize: 9, color: agColor, border: `1px solid ${agColor}55`, borderRadius: 3, padding: '1px 6px', fontFamily: "'DM Mono',monospace", letterSpacing: 1, flexShrink: 0 }}>{team.ageGroup}</span>
                                )}
                                <span style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 16, letterSpacing: 2, color: C.gold }}>{team.teamName}</span>
                              </div>
                              <div style={{ padding: '10px 12px' }}>
                                {team.hasWidget ? (
                                  <div id={team.gcWidgetDivId} />
                                ) : (
                                  <div style={{ fontSize: 11, color: C.textDim, lineHeight: 1.5 }}>
                                    <div style={{ marginBottom: 4, fontSize: 9, color: C.textDim + '88', fontFamily: "'DM Mono',monospace" }}>Configure GameChanger widget in Manage → Teams to show live schedule</div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Public Athlete Profiles — grouped by class year */}
            {players.length > 0 ? (
              <div>
                <div style={{ fontSize: 10, color: C.textDim, letterSpacing: 3, marginBottom: 20, fontFamily: "'DM Mono',monospace" }}>
                  ATHLETE PROFILES — {players.length} PUBLIC
                </div>
                <ClassYearGroups classYears={classYears} playersByClass={playersByClass} />
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: C.textDim, fontSize: 13, border: `1px dashed ${C.greenBorder}`, borderRadius: 8 }}>No public athlete profiles yet.</div>
            )}

            {/* Alumni — graduated players, same treatment as Athlete Profiles, own heading, only
                rendered when there are any */}
            {alumniPlayers.length > 0 && (
              <div style={{ marginTop: 28 }}>
                <div style={{ fontSize: 10, color: C.textDim, letterSpacing: 3, marginBottom: 20, fontFamily: "'DM Mono',monospace" }}>
                  ALUMNI — {alumniPlayers.length}
                </div>
                <ClassYearGroups classYears={alumniClassYears} playersByClass={alumniByClass} />
              </div>
            )}

            <div style={{ textAlign: 'center', padding: '28px 0 8px', fontSize: 11, color: C.textDim }}>
              Powered by <a href="/" style={{ color: C.textDim, textDecoration: 'none' }}>Warrior Academy</a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
